import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch, useHistory } from "react-router-dom";
import _ from "lodash";
import cx from "classnames";
import Select, { components } from 'react-select';

import { Modal, Button, Col, Row, Form, Image, Offcanvas, Dropdown, Spinner } from 'react-bootstrap';

import { fetchFigmaFrames, invalidateFigma, updateSelection } from 'actions/integrations/figma';
import { createStoryboardFromFigma } from 'actions/storyboard';
import FontDialog from 'components/Font/upload';
import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { isUrl } from 'services/asset.service';
import { getBaseUrl } from 'services/util';
import styles from './figma.module.scss';

export const FigmaLinkModal = ({ show, onDiscard, updateProperty }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const [figmaLink, setFigmaLink] = useState('');
  const loading = useSelector(state => _.get(state, 'integrations.figma.figmaFile.loading', false));
  const success = useSelector(state => _.get(state, 'integrations.figma.figmaFile.success', false));
  const error = useSelector(state => _.get(state, 'integrations.figma.figmaFile.error', false));
  const [errors, setErrors] = useState(false);

  const activatedIntegratons = useSelector(state => _.get(state, 'integrations.activated.items', []));
  const isFigmaIntegrationActive = activatedIntegratons.some(integration => integration.type === "figma");

  useEffect(() => {
    if(!show){
      setFigmaLink('');
      setErrors(false);
    } 
  }, [show]);

  useEffect(()=>{
    if(!loading && success){
      setFigmaLink("");
      updateProperty('showFramesModal', true);
      return
    }
  },[success])

  useEffect(()=>{
    if(!loading && error){
      setErrors(true);
      return
    }
  },[error])

  const handleInputChange = (e) => {
    let { value } = e.target;
    setFigmaLink(value);
  }

  const handleImport = () => {
    if (!isValidFigmaLink(figmaLink)) {
      setErrors(true);
      return;
    }

    setErrors(false);
    const fileIdRegex = /\/file\/([^/]+)/;
    const fileIdMatch = figmaLink.match(fileIdRegex);
    const fileId = fileIdMatch ? fileIdMatch[1] : null;

    dispatch(fetchFigmaFrames(fileId, figmaLink));
  }

  const isValidFigmaLink = (link) => {
    const figmaRegex = /^https:\/\/www\.figma\.com\/(file|proto)\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+/;
    return figmaRegex.test(link) && isUrl(figmaLink)
  };

  return (
    <Modal backdrop='static'
      className={styles.importFigmaModal}
      contentClassName={styles.importFigmaContent}
      dialogClassName={styles.importFigmaDialog}
      backdropClassName={styles.backdropImportFigma}
      show={show}
      centered size="lg"
    >
      <button className={styles.closeBtn} onClick={onDiscard} disabled={loading}>
          <i className={cx('cleverstory-close')} />
      </button>
      <Modal.Body className={styles.importFigmaBody}>
        <Row className={styles.contentRow}>
          {isFigmaIntegrationActive ?
            <Col className={styles.contentContainer} xs={6}>
              <div>
                <h2 className={styles.title}>Import Figma file</h2>
                <span className={styles.description}> Enter the figma Page link below and ensure that you have “view” or “edit” access.</span>
                <Form.Group className={styles.inputForm}>
                    <Form.Control 
                      className={cx(styles.formInput, {[styles.active] : _.size(figmaLink) > 0, [styles.error] : errors})} 
                      onChange={handleInputChange} 
                      value={figmaLink} 
                      disabled={loading}
                      placeholder='Enter the link here'
                    />
                    <Button className={styles.importBtn} variant={(loading || _.size(figmaLink) == 0) ? "secondary" : "primary"} onClick={handleImport}>
                      Import
                    </Button>
                </Form.Group>
                {errors && <span className={styles.errorMsg}>Invalid link, Please enter a valid link</span>}
                {loading && 
                  <span className={styles.loadingMsg}> 
                    <Spinner className={styles.dot} animation="border" size="sm"/>
                    Just a moment, fetching frames... 
                 </span>
                }
              </div>

              <a className={styles.infoLink} target="_blank" href='https://help.figma.com/hc/en-us/articles/360040531773-Share-files-and-prototypes'>
                <i className={cx("cleverstory-help")} /> 
                <span>How to get a link to a Figma file?</span>
              </a>
            </Col>
            :
            <Col className={styles.contentContainer} xs={6}>
              <h2 className={styles.title}>Import Figma file</h2>
              <div>
                <span className={styles.description}> Ooops! <br/>Before you bring your Figma designs, make sure you have the Figma integration activated.</span>
                
                <Button variant="primary" className={cx(styles.integrationBtn, 'mt-3')} 
                  onClick={()=> { history.push({ pathname: `${baseUrl}/${`integration-settings/integration`}`})}}>
                   Activate Integration
                </Button>
              </div>
            </Col>
          }
          <Col className={styles.imageContainer} xs={6}>
            <Image src={'/images/createFigma.png'} className={styles.illustrationImg} />
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export const FigmaFramesModal = ({ show, onDiscard, updateProperty }) => {

  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const figmaFile = useSelector(state => _.get(state, 'integrations.figma.figmaFile', null));
  const allPages = _.get(figmaFile, 'pages', []);
  const fileName = _.get(figmaFile, 'fileName', null);
  const figmaLink = _.get(figmaFile, 'fileLink', null);
  
  const [pagesMenuOptions, setPagesMenuOptions] = useState([]);
  const [currentPage, setSelectedPage] = useState(null);

  const [frames, setFrames] = useState([]);
  const [selectedFrames, setSelectedFrames] = useState([]);

  useEffect(()=>{
    if(!allPages){
      return
    }
    const pagesOptions = (allPages || []).map(page => {
      return {
        label: page.pageName,
        value: page.pageId,
        id: page.pageId
      }
    });

    if(pagesOptions && pagesOptions.length > 0){
      setPagesMenuOptions(pagesOptions);
      setSelectedPage(pagesOptions[0])
    }
  },[allPages])

  useEffect(()=>{
    getFramesByPage(currentPage);
  },[currentPage])

  useEffect(() => {
    if(!show){
      setSelectedFrames([]);
      dispatch(invalidateFigma());
    }
  }, [show]); 

  const getFramesByPage = (selectedPage) => {
    const selectedPageId = _.get(selectedPage, 'value', null);
    if(selectedPageId){
      const page = allPages.find(page => page.pageId == selectedPageId);

      const _frames = page && page.frames.map(frame => {
          return {
            frameId: frame.frameId,
            name: frame.name,
            thumbnailUrl: frame.thumbnailUrl,
            pageId: page.pageId,
            pageName: page.pageName
          };
      }).flat();

      setFrames(_frames);

      const container = containerRef.current;
      if (container) {
        container.scrollTop = 0;
        container.scrollLeft = 0;
      }
    }
  }

  const SelectedFrames = ({ page, deleteFrame}) => {
    const frames = _.get(page, 'frames', []);
    const pageId = _.get(page, 'id', null);

    return frames.map((frame)=>{
      return(
        <div className={styles.selectedFrame}>
          <div className={styles.thumbnailContainer}>
            <img className={styles.thumbnail} src={frame.thumbnailUrl} />
          </div>
          <span className={styles.name}>{frame.name}</span>
          <span className={styles.close} onClick={()=> deleteFrame(pageId, frame.frameId)}>
            <i className="cleverstory-close"></i>
          </span>
        </div>
      )
    })
  }

  const deleteFrame = (id, frameId) => {
    setSelectedFrames(prevFrames => {
        return prevFrames.map(frame => {
            if (frame.id === id) {
                frame.frames = frame.frames.filter(f => f.frameId !== frameId);
          
                if (frame.frames.length === 0) {
                  return null;
                }
            }
            return frame;
        }).filter(Boolean);
    });
  };

  return (
    <Modal className={styles.figmaManagerModal} backdropClassName={styles.backdropFigmaFrames} show={show} centered>
      <Modal.Header className={styles.header}>
      <div className={styles.fileInfo}>
        <span className={styles.fileName}>{fileName}</span>
        <a className={styles.figmaLink} href={figmaLink} target='_blank'><i className='cleverstory-url'/></a>
      </div>
    </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.tabSection}>
          <div className={styles.framesGallery}>
            <header className={styles.header}>
              <h6 className={styles.sectionTitle}>Select Frames:</h6>
              <aside className={styles.filterContainer}>
                <label className={styles.filterLabel}>Pages:</label>
                <Dropdown className={styles.storboardTypeDropdown}>
                  <Dropdown.Toggle as={CustomFilterDropdown} id="dropdown-custom-components">
                    {_.get(currentPage, 'label', '')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {
                      (pagesMenuOptions || []).map((option, index)=> {                    
                        return (
                          <Dropdown.Item active={option.value == currentPage && currentPage.value} onClick={ ()=>setSelectedPage(option) }  key={index} eventKey={index}>{option.label}</Dropdown.Item>
                        )
                      })
                    }
                  </Dropdown.Menu>
                </Dropdown>
              </aside>
            </header>

            <ScrollableFramesContainer frames={frames} selectedFrames={selectedFrames} setSelectedFrames={setSelectedFrames} pagesMenuOptions={pagesMenuOptions} containerRef={containerRef}/>
          </div>
          <div className={styles.selectedFramesSection}>
            <h6 className={styles.sectionTitle}>Selected Frames:</h6>
            <div className={styles.collection}>
            {(selectedFrames || []).map((page) => {
              return (
                <>
                  <span className={styles.pageTitle}>{page.name}</span>
                  <SelectedFrames page={page} deleteFrame={deleteFrame}/>
                </>
              )
            })}
            </div> 
          </div>
        </div>
      </Modal.Body>

      <Footer allPages={allPages} selectedFrames={selectedFrames} updateProperty={updateProperty} onDiscard={onDiscard}/>
    </Modal>
  )
}

export const FontsModal = ({ show, onDiscard, updateProperty }) => {
  const dispatch = useDispatch();

  const availableFonts = useSelector(state => _.get(state, 'fonts.items'));
  const figmaFonts = useSelector(state => _.get(state, 'integrations.figma.figmaFile.fonts', []));
  const selectedFonts = useSelector(state => _.get(state, 'integrations.figma.figmaFile.selected.fonts', []));

  const defaultFont = availableFonts.find(font => font.name == "Montserrat");

  const handleSelectedFont = (missingFont, selectedOption) => {
    const updatedFonts = selectedFonts.map(font => {
      if (font.missingFont === missingFont) {
        return { missingFont, selectedFont: selectedOption };
      }
      return font;
    });

    dispatch(updateSelection({ fonts: updatedFonts }));
  }

  const handleSave = () => {
    updateProperty('showContentTypePanel', true);
    onDiscard();
  }

  useEffect(() => {
    if(figmaFonts && _.size(figmaFonts) > 0){
      const defaultSelectedFonts = (figmaFonts || []).map((missingFont) => ({
        missingFont: missingFont,
        selectedFont: {
          label: defaultFont.name,
          value: defaultFont.fontFamily,
          font: defaultFont
        }
      }));

      if (selectedFonts.length === 0) {
        dispatch(updateSelection({ fonts: defaultSelectedFonts }));
      }
    }
  }, [figmaFonts]);

  return (
    <>
    <Modal backdrop='static'
      className={styles.chooseFontsModal}
      contentClassName={styles.chooseFontsContent}
      dialogClassName={styles.chooseFontsDialog}
      backdropClassName={styles.backdropFigmaFonts}
      show={show}
      centered size="lg"
    >
      <button className={styles.closeButton} onClick={onDiscard}>
          <i className={cx('cleverstory-close', styles.closeButtonIcon)} />
      </button>
      <Modal.Body className={styles.chooseFontsBody}>
        <Row className={styles.contentRow}>
          <Col className={styles.contentContainer} xs={12}>
            <div>
              <h2 className={styles.title}>Choose Fonts</h2>
              <p className={styles.description}> Choose fonts from the Cleverstory fonts library or upload from desktop.</p>

              <div className={styles.fontFamilySection}>
                <div className={styles.headerRow}>
                    <span className={styles.missingFont}>Missing Font</span>
                    <span className={styles.arrowIcon}></span>
                    <span className={styles.choosefonts}>Choose/Upload Font</span>
                </div>
                {
                  (selectedFonts || []).map(font =>{
                    return (
                      <div className={styles.fontRow}>
                        <div className={styles.missingFont}>{font.missingFont}</div>
                        <span className={styles.arrowIcon}><i className='cleverstory-back'/></span>
                        <div className={styles.choosefonts}>
                            <FontFamilyDropdown availableFonts={availableFonts} font={font} 
                            handleSelectedFont={handleSelectedFont}/>
                        </div>
                      </div>
                  )})
                }
              </div>
              
              <div className={styles.actionContainer}>
                <Button className={styles.doneBtn} onClick={handleSave}>Done</Button>
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
</>
  )
}

export const ChooseContentTypePanel = ({show, onDiscard, resetAllModals}) => {
  
  const dispatch = useDispatch();
  const workspace = useSelector(state => _.get(state, `workspaces.workspace.item.id`));
  const figmaFile = useSelector(state => _.get(state, 'integrations.figma.figmaFile', null));
  const fileUid = _.get(figmaFile, 'fileUid', null);
  const fileName = _.get(figmaFile, 'fileName', null);
  const selectedFonts = _.get(figmaFile, 'selected.fonts', []);
  const selectedFrames = _.get(figmaFile, 'selected.frames', []);

  const subscription = useSelector(state => _.get(state, 'workspaces.workspace.item.subscription'));

  const [storyboardName, setStoryboardName] = useState('');
  const [content, setContent] = useState(null);
  const contentId = _.get(content, 'id', null);

  const contentTypes = [
    { 
      id: 'blank_interactive_template',
      type: "INTERACTIVE_CONTENT",
      label: "Interactive Content",
      image: "/images/interactive.svg",
      active: "/images/interactive_active.svg",
      className: "interactiveImg",
      enabled: _.get(subscription, 'interactiveContentEnabled', false)
    },
    {
      id: 'blank_landing_page_template',
      type: "LANDING_PAGE",
      label: "Landing Page",
      image: "/images/landing.svg",
      active: "/images/landing_active.svg",
      className: "landingImg",
      enabled: _.get(subscription, 'landingPageEnabled', false)
    },
    {
      id: 'blank_resource_center_template',
      type: "RESOURCE_CENTER",
      label: "Resource Center",
      image: "/images/resource.svg",
      active: "/images/resource_active.svg",
      className: "resourceImg",
      enabled: _.get(subscription, 'resourceCenterEnabled', false)
    }
  ];

  useEffect(() => {
    setStoryboardName(fileName)
  }, [fileName]);

  useEffect(() => {
    if(!show){
      setStoryboardName(fileName || '');
      setContent(null);
    }
  },[show])

  const handleSave = () => {
    const contentId = _.get(content, 'id', null);
    const contentType = _.get(content, 'label', null);

    const processedFrames = selectedFrames.reduce((acc, obj) => {
      acc[obj.id] = obj.frames.map(frame => frame.frameId);
      return acc;
    }, {});

    const processedFonts = selectedFonts.reduce((acc, obj) => {
      acc[obj.missingFont] = _.get(obj, 'selectedFont.font.id', null);
      return acc;
    }, {});

    const request = {
      "fileUid": fileUid,
      "type": contentId,
      "name": storyboardName,
      "fonts": processedFonts,
      "frames": processedFrames,
      "storyboardType": contentType
    }

    dispatch(createStoryboardFromFigma(fileUid, request, workspace));
    resetAllModals();
    dispatch(invalidateFigma());
  }

  const onNameChange = (e) => {
    const { value } = e.target;
    setStoryboardName(value);
  }

  return(
    <Offcanvas className={styles.chooseStoryboardPanel} show={show} onHide={onDiscard} backdropClassName={styles.backdropFigmaContentType} 
      placement='start' backdrop="static"> 
        <div className={styles.createStoryboardPanel}>
          <Button className={styles.backButton} variant="info" onClick={onDiscard} >
            <i className={cx("cleverstory-close")} />
          </Button>
          <header className={styles.header}>
            <h1 className={styles.title}>Choose a content type</h1>
            <span className={styles.description}>Select a content type to create a storyboard effortlessly with your figma design.</span>
          </header>
          <div className={styles.blankTemplate}>
            <div className={styles.customContainer}>
                <h6 className={styles.typesTitle}>TITLE</h6>
                <Form.Control
                  value={storyboardName}
                  onChange={onNameChange}
                  className={styles.name}
                  placeholder="Storyboard name"
                />
            </div>

            <div className={styles.customContainer}> 
                <h6 className={styles.typesTitle}>CONTENT TYPE</h6>     
                <div className={styles.storyBoardFormat} >

                {(contentTypes || []).map((option)=> {
                    return(
                      <>
                      {option.enabled &&
                        <Button variant="info" className={cx(styles.scratchTemplate, { [styles.activeBtn]: _.get(content, 'type', '') === option.type })} 
                        onClick={() => setContent(option)}
                        >
                          <div className={styles.imgBox}>
                            <img className={styles[option.className]} src={_.get(content, 'type', '') === option.type ? option.active : option.image} />
                          </div>
                          <div className={styles.titleBox}>
                            <span>{option.label}</span>
                          </div>
                        </Button>
                      }
                      </>
                    )
                  })
                }
                </div>
            </div>
            
            <div className={styles.actionContainer}>
              <Button onClick={handleSave} className={styles.saveButton} variant="primary" disabled={!storyboardName || !contentId}>
                Continue
              </Button>
              <Button onClick={onDiscard} className={styles.saveButton} variant="secondary" >
                Cancel
              </Button>
            </div>
      
          </div>
        </div>
    </Offcanvas>
  )
}

const CustomFilterDropdown = React.forwardRef(({ children, onClick }, ref) => (
  <span
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className={styles.dropDownToggle}
  >
    {children}
    <i className={cx('cleverstory-drop-down')}/>
  </span>
));

const FontFamilyDropdown = ({availableFonts, font, handleSelectedFont}) => {

  const selectRef = useRef(null);
  const [fontOptions, setFontOptions] = useState([]);
  const [uploadFont, setUploadFont] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    var fonts = availableFonts.map(font => {
      return {
        label: font.name,
        value: font.fontFamily,
        font: font
      }
    });

    fonts = _.orderBy(fonts, 'label', 'asc');
    setFontOptions(fonts);
  }, [availableFonts]);

  const FamilyDropdownOption = (props) => {
    Fonts.useFont([props.data.font]);

    return (
      <components.Option {...props} className={styles.fontFamilyOption}>
        <div style={{
          fontFamily: props.data.value,
        }}>{props.children}</div>
      </components.Option>
    );
  }

  const FamilyMenu = (props) => {
    const { onFontUpload } = props.selectProps;
  
    return (
      <components.Menu {...props} className={styles.fontFamilyMenu}>
        {props.children}
        <button className={styles.addFont} onClick={()=>onFontUpload()}>
          <i className='cleverstory-upload' /> Upload fonts
        </button>
      </components.Menu>
    );
  }

  const DropdownIndicator = props => {
    return (
      <components.DropdownIndicator {...props}>
        <i className={'cleverstory-drop-down'} />
      </components.DropdownIndicator>
    );
  };

  const filteredOptions = (options, query) => {
    const input = query.toLowerCase();

    if (!options) {
      return
    }

    let startingLettersMatch = options.filter(option => {
      const label = option.label ? option.label.toLowerCase() : '';
      return label.startsWith(input);
    });

    let includesLettersMatch = options.filter(option => {
      const label = option.label ? option.label.toLowerCase() : '';
      return label.includes(input) && !label.startsWith(input);
    });

    return [...startingLettersMatch, ...includesLettersMatch];
  };

  const handleQueryChange = (query) => {  
    setQuery(query);
  }

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#EEEEEE' : '#ffffff',
    }),
  }

  return(
    <>
      <div className={styles.dropdownMenuContainer}>
        <Select
          className={cx(styles.dropdown)}
          classNamePrefix={"dropdown"}
          styles={customStyles}
          options={filteredOptions(fontOptions, query)}
          onChange={(selectedOption)=>handleSelectedFont(font.missingFont, selectedOption)}
          value={font.selectedFont}
          defaultValue={font.selectedFont}
          components={{
            Option: FamilyDropdownOption,
            Menu: FamilyMenu,
            DropdownIndicator: DropdownIndicator
          }}
          inputValue={query}
          onInputChange={handleQueryChange}
          placeholder={'Select Font...'}
          onFontUpload={() => {
            setUploadFont(true);
          }}
          isSearchable={true}
          ref={selectRef}
        />
      </div>

      <FontDialog show={uploadFont} onClose={() => setUploadFont(false)}/>
    </>
  )
}

const ScrollableFramesContainer = ({frames, selectedFrames, setSelectedFrames, pagesMenuOptions, containerRef}) => {
  const [showLeftBtn, setShowLeftBtn] = useState(false);
  const [showRightBtn, setShowRightBtn] = useState(true);

  const addOrUpdateFrame = (item) => {
    const newFrame = {
      id: item.pageId,
      name: item.pageName,
      frames: [{
        frameId: item.frameId,
        name: item.name,
        thumbnailUrl: item.thumbnailUrl
      }]
    }

    if(newFrame){
      setSelectedFrames(prevFrames => {
        let updatedFrames = [...prevFrames];
        let existingElement = _.find(updatedFrames, { 'id': newFrame.id });

        if (existingElement) {
          let index = _.findIndex(existingElement.frames, { 'frameId': newFrame.frames[0].frameId });
          if (index !== -1) {
            existingElement.frames.splice(index, 1);

            if (existingElement.frames.length === 0) {
              updatedFrames = updatedFrames.filter(frame => frame.id !== existingElement.id);
            }
          } else {
            existingElement.frames.push(newFrame.frames[0]);
          }
        } else {
          updatedFrames.push(newFrame);
        }

        const order = pagesMenuOptions.map(page => page.id);

        updatedFrames = _.map(order, id => _.find(updatedFrames, { 'id': id }));
        updatedFrames = _.compact(updatedFrames);

        return updatedFrames
      });
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [frames]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      setShowLeftBtn(container.scrollLeft > 0);
      setShowRightBtn(container.scrollLeft + container.clientWidth < container.scrollWidth);
    }
  };
  
  const navigationScroll = (direction) => {
    const container = containerRef.current;
    if (container) {
      const scrollStep = (230 + 10) * 4;
      const currentScroll = container.scrollLeft;
  
      if (direction === 'right') {
        const remainingSpace = currentScroll % (230 + 10);
        const adjustedScroll = remainingSpace !== 0 ? scrollStep - remainingSpace : scrollStep;
        container.scrollBy({
          left: adjustedScroll,
          behavior: 'smooth',
        });
      } else if (direction === 'left') {
        const remainingSpace = (230 + 10) - (currentScroll % (230 + 10));
        const adjustedScroll = remainingSpace !== (230 + 10) ? remainingSpace - scrollStep : -scrollStep;
        container.scrollBy({
          left: adjustedScroll,
          behavior: 'smooth',
        });
    }}
  };

  return(
    <div className={styles.pageGallery} ref={containerRef}>
      {
        (frames || []).map((frame)=>{
          const AllFrameIds = _.flatMap(selectedFrames, 'frames').map(frame => frame.frameId);
          const selected = AllFrameIds.some(id => id.includes(frame.frameId));

          return <FigmaFrame key={frame.frameId} frame={frame} selected={selected} addOrUpdateFrame={addOrUpdateFrame} />
        })
      }

      {showLeftBtn &&
        <Button variant="secondary" className={cx(styles.navigationBtn, styles.leftBtn)} onClick={()=>navigationScroll('left')}>
          <i className={cx('cleverstory-drop-down-left')} />
        </Button>
      }
      {showRightBtn &&
        <Button variant="secondary" className={cx(styles.navigationBtn, styles.rightBtn)} onClick={()=>navigationScroll('right')}>
          <i className={cx('cleverstory-drop-down-right')} />
        </Button>
      }
    </div>
  )
}

const FigmaFrame = ({frame, selected, addOrUpdateFrame}) => {
  const imageRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      setLoaded(true);
    }
  }, [imageRef]);

  return (
    <div className={styles.frame} onClick={()=>addOrUpdateFrame(frame)}>
      <span className={styles.pageTitle}>{frame.name}</span>
      <div className={cx(styles.pageThumbnail,{ [styles.selected] : selected})}>
        { !loaded && 
          <div class={styles.placeholder}></div> 
        }
        <img ref={imageRef} src={frame.thumbnailUrl} className={cx(styles.thumbnailImg, {[styles.showImg] : loaded})} onLoad={()=>setLoaded(true)}/>
      </div>
    </div>
  )
}

const Footer = ({allPages, selectedFrames, updateProperty, onDiscard}) => {
  const dispatch = useDispatch();
  
  const getTotalCount = (pages) => {
    return _.sumBy(pages, page => {
        return Array.isArray(page.frames) ? page.frames.length : 0;
    });
  }

  const onContinue = () => {
    const request = {
      frames: selectedFrames
    }
    dispatch(updateSelection(request));
    updateProperty('showFontsModal', true);
  }

  const onCancel = () => {
    onDiscard();
    updateProperty('showLinkModal', false);
  }

  return(
    <Modal.Footer className={styles.modalFooter}>
      <div className={styles.footer}>
        <span className={cx(styles.count)}>
          Selected Frames: {`${getTotalCount(selectedFrames)}/${getTotalCount(allPages)}`} 
        </span>
        <div className={styles.actionContainer}>
          <Button size="sm" className={styles.saveButton} variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" className={styles.saveButton} variant="primary" onClick={onContinue} 
            disabled={_.size(selectedFrames) == 0} >Continue
          </Button>
        </div>
      </div>
    </Modal.Footer>
  )
}